import clsx from 'clsx'
import React from 'react'
import {useIntl} from 'react-intl'
import {useLocation, useNavigate} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {useAuth} from '../../../../app/modules/auth'
import {KTSVG, checkIsActive, checkIsActiveSubRoute, isFeatureEnabled} from '../../../helpers'
import UseRoutesPermission from '../../../hooks/UseRoutesPermission'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import {useAsideProvider} from '../../../providers/AsideProvider'
import {first, isArray} from 'lodash'
import {FEATURES, ROUTES} from '../../../constants'
import {
  isActiveEnterpriseManager,
  isEnterpriseOwner,
} from '../../../../app/modules/auth/core/userHelpers'

const MenuItem = ({item, idx = null, isChild = false, className = ''}) => {
  const navigate = useNavigate()
  const {isMobileTablet, isWindows} = useWindowDimensions()
  const {activeFooter, setActiveFooter} = useAsideProvider()
  const {pathname} = useLocation()
  const {hasShow, to, icon, title, children} = item
  const isActive =
    (typeof to === 'string' ? checkIsActive(pathname, to) : checkIsActiveSubRoute(to, pathname)) &&
    !activeFooter

  const handleItemClick = () => {
    const path = isArray(to) ? first(to) : to
    if (path) {
      navigate(path)
    }
    setActiveFooter(null)
  }

  if (!hasShow) {
    return null
  }

  return (
    <div
      key={idx}
      className={clsx(`${className}__center__item`, {
        active: isActive,
        child: isChild,
      })}
    >
      <div className='mb-2' onClick={handleItemClick}>
        <div
          className={clsx('accordion-header d-flex align-items-center', {
            child: isChild,
            'mx-2': isChild && isMobileTablet,
            'bg-primary rounded-2 py-1': isChild && isActive && isMobileTablet,
            collapsed: !isActive,
          })}
          data-bs-toggle='collapse'
          data-bs-target={`#kt_accordion_menu_main_item_${idx}`}
          data-kt-menu-trigger='click'
        >
          <KTSVG
            path={icon}
            small={false}
            className={clsx(`${className}__center__item__icon`, {
              child: isChild,
            })}
            svgClassName={(isMobileTablet && 'mh-40px') || (isWindows && 'mh-50px') || ''}
          />
          <span
            className={clsx(`flex-fill fs-5 fs-xxl-6 ${className}__center__item__title fw-bolder`, {
              child: isChild,
            })}
          >
            {title}
          </span>
        </div>
        {children && (
          <div
            id={`kt_accordion_menu_main_item_${idx}`}
            className={clsx('accordion-body fs-6 collapse pb-1', {show: isActive})}
            data-bs-parent={`#kt_accordion_menu_main_${className}`}
          >
            {children.map((childItem, childIdx) => (
              <MenuItem key={childIdx} item={childItem} isChild className={className} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const Menu = ({configMenu, className = ''}) => {
  const {setHasShowSubmenu, activeFooter} = useAsideProvider()

  const handleClickMenu = () => {
    if (!activeFooter) return
    setHasShowSubmenu(true)
  }

  return (
    <div
      className='accordion accordion-icon-toggle'
      id={`kt_accordion_menu_main_${className}`}
      onClick={handleClickMenu}
    >
      {configMenu.map((menuItem, idx) => (
        <MenuItem key={idx} idx={idx} item={menuItem} className={className} />
      ))}
    </div>
  )
}

type Props = {
  className: string
}

const AsideMenuLayout: React.FC<Props> = ({className = ''}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const {routes} = UseRoutesPermission()
  const canAccessOrganizations =
    isActiveEnterpriseManager(currentUser) || isEnterpriseOwner(currentUser)

  const configMenu = [
    {
      hasShow: currentUser && canAccessOrganizations,
      to: ROUTES.ORGANIZATIONS.BASE,
      icon: '/media/gori/aside/orders-light.svg',
      title: intl.formatMessage({id: 'ORGANIZATION'}),
    },
    {
      hasShow: currentUser && routes.ORDERS_VIEW.hasPermission,
      to: [ROUTES.ORDERS.BASE],
      icon: '/media/gori/aside/orders-light.svg',
      title: intl.formatMessage({id: 'ORDERS'}),
    },
    {
      hasShow: currentUser && routes.BATCHES_VIEW.hasPermission,
      to: ROUTES.BATCHES.BASE,
      icon: '/media/gori/aside/batches.svg',
      title: intl.formatMessage({id: 'BATCHES'}),
    },
    {
      hasShow: currentUser && routes.SHIPMENTS_VIEW.hasPermission,
      to: ROUTES.SHIPMENTS.BASE,
      icon: '/media/gori/aside/shipments.svg',
      title: intl.formatMessage({id: 'SHIPMENTS'}),
    },
    {
      hasShow: currentUser && routes.SHIPMENTS_VIEW.hasPermission,
      to: [ROUTES.END_OF_DAY.OPEN_SHIPMENTS, ROUTES.END_OF_DAY.CLOSED_SHIPMENTS],
      icon: '/media/gori/aside/manifest.svg',
      title: intl.formatMessage({id: 'END_OF_DAY'}),
    },
    {
      hasShow: currentUser && routes.CLAIMS_VIEW.hasPermission,
      to: ROUTES.CLAIMS.BASE,
      icon: '/media/gori/aside/claims.svg',
      title: intl.formatMessage({id: 'CLAIMS'}),
    },
    {
      hasShow: currentUser,
      to: ROUTES.REPORTS.BASE,
      icon: '/media/gori/aside/logs.svg',
      title: intl.formatMessage({id: 'REPORTS'}),
    },
    {
      hasShow:
        currentUser && routes.DASHBOARD.hasPermission && isFeatureEnabled(FEATURES.DASHBOARD),
      to: ROUTES.DASHBOARD.BASE,
      icon: '/media/gori/aside/analytic.svg',
      title: intl.formatMessage({id: 'ANALYTICS'}),
    },
    {
      hasShow: currentUser && routes.BATCHES_VIEW.hasPermission,
      to: ROUTES.A1.BASE,
      icon: '/media/gori/aside/A1.svg',
      title: 'A1',
    },
    {
      hasShow: currentUser && routes.SYSTEM_ADMIN.hasPermission,
      to: '/admin',
      icon: '/media/gori/aside/admin.svg',
      title: intl.formatMessage({id: 'ADMIN'}),
    },
  ]

  return (
    <CSSTransition appear in timeout={300} classNames='aside-menu-fade' unmountOnExit>
      <Menu configMenu={configMenu} className={className} />
    </CSSTransition>
  )
}

export {AsideMenuLayout}
